import {naturalSort} from '@benchling/js-utils';
import {scalePoint, scaleUtc} from 'd3';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {sum} from 'BenchlingUtil';
import {DataType} from 'data-frames/DataFrameTypes';
import {mean} from 'ui-library/charting/statistics/StatUtils';
import {AnalysisStatus} from 'ui-library/charting/statistics/Types';
import {NumericCartesianPoint} from 'ui-library/charting/Types';
import getDateDomain from 'ui-library/charting/util/getDateDomain';
import {assertNever} from 'util/Types';

import {R_SQUARED} from './Constants';
import prettyExpression from './prettyExpression';
import {RegressionAnalysis, RegressionDataset, RegressionXAxisTSTypes} from './RegressionTypes';

interface LinearRegressionModelFit {
  B0: number;
  B1: number;
}

export type LinearRegressionResults = LinearRegressionModelFit & {
  R2: number;
};

export type LinearRegressionAnalysis = RegressionAnalysis<LinearRegressionResults>;

/**
 * Performs a simple linear regression analysis on the given data including estimating the model parameters and
 * calculating related statistics.
 *
 * Expects a dataset containing a list of points with x y values where x is the independent variable, and
 * y is the dependent variable. The output is a LinearRegressionAnalysis object containing the fitted parameters,
 * statistics on the model, and guaranteed fields from RegressionAnalysis. In the output parameters, B0 is
 * the intercept and B1 is the slope.
 *
 * The linear regression is fitted with Ordinary Least Squares (OLS), and only supports one independent
 * variable.
 *
 * See the section about fitting the model on this Wikipedia article for more details about the algorithm
 * https://en.wikipedia.org/wiki/Simple_linear_regression
 */
export function performLinearRegressionAnalysis(dataset: RegressionDataset): LinearRegressionAnalysis {
  const [data, dataToEstimateInput] = getDataAndDataToEstimateInput(dataset);

  const dataError = validateData(data);
  if (dataError != null) {
    return {
      message: dataError,
      status: AnalysisStatus.ERROR,
    };
  }
  const meanX = mean(data.map((point) => point.x));
  const meanY = mean(data.map((point) => point.y));

  const modelFit = fitLinearRegressionModel(data, meanX, meanY);
  const R2 = calculateR2(data, meanY, modelFit);

  return {
    result: {
      getEstimatedY: (x) => estimatePointY(dataToEstimateInput(x), modelFit),
      fitEquation: getFitEquation(modelFit.B0, modelFit.B1),
      fitOutput: {...modelFit, R2},
      outputValues: [
        {fieldName: 'm', showInPlotHeader: false, value: modelFit.B1},
        {fieldName: 'b', showInPlotHeader: false, value: modelFit.B0},
        {fieldName: 'R2', fieldNameDisplayOverride: R_SQUARED, showInPlotHeader: true, value: R2},
      ],
      tooltipDisclaimer: null,
    },
    status: AnalysisStatus.SUCCESS,
  };
}

export function getDataAndDataToEstimateInput(
  dataset: RegressionDataset
): [Array<NumericCartesianPoint>, (x: RegressionXAxisTSTypes) => number] {
  let data;
  let dataToEstimateInput: (x: RegressionXAxisTSTypes) => number;
  const xValues = dataset.points.map(({x}) => x);

  if (dataset.axisTypes.x === DataType.NUMBER) {
    data = dataset.points as Array<NumericCartesianPoint>;
    dataToEstimateInput = (x: number) => x;
  } else if (dataset.axisTypes.x === DataType.STRING) {
    // eslint-disable-next-line no-restricted-syntax
    const xLabels = naturalSort(_.uniq(xValues) as Array<string>);
    const scale = scalePoint()
      .domain(xLabels)
      .range([0, xLabels.length - 1]);

    data = dataset.points.map(({x, y}) => ({
      x: scale(x as string),
      y,
    })) as Array<NumericCartesianPoint>;
    dataToEstimateInput = (x: string) => scale(x)!;
  } else if (dataset.axisTypes.x === DataType.DATE_OR_TIMESTAMP) {
    const scale = scaleUtc().domain(getDateDomain(xValues as Array<string>));

    data = dataset.points.map(({x, y}) => ({
      x: scale(new Date(x as string)),
      y,
    })) as Array<NumericCartesianPoint>;
    dataToEstimateInput = (x: Date) => scale(x);
  } else if (dataset.axisTypes.x === DataType.BOOLEAN || dataset.axisTypes.x === DataType.JSON) {
    throw new Error(`Unsupported axis type ${dataset.axisTypes.x}`);
  } else {
    throw new Error(`Unsupported axis type ${assertNever(dataset.axisTypes.x)}`);
  }

  return [data, dataToEstimateInput];
}

export function validateData(data: Array<NumericCartesianPoint>): string | null {
  if (data.length <= 1) {
    return 'Data must have at least 2 points to compute linear regression';
    // eslint-disable-next-line no-restricted-syntax
  } else if (_.uniq(data.map((dataPoint) => dataPoint.x)).length <= 1) {
    return 'Data must have at least 2 different x values to compute linear regression';
  }

  return null;
}

function fitLinearRegressionModel(
  data: Array<NumericCartesianPoint>,
  meanX: number,
  meanY: number
): LinearRegressionModelFit {
  const slopeResult = fitSlope(data, meanX, meanY);
  const interceptResult = fitIntercept(meanX, meanY, slopeResult);

  return {
    B0: interceptResult,
    B1: slopeResult,
  };
}

export function fitSlope(data: Array<NumericCartesianPoint>, meanX: number, meanY: number): number {
  const totalXYMean = data.length * meanX * meanY;
  const totalSquareXMean = data.length * meanX ** 2;

  const ssXY = sum(data.map((point) => point.x * point.y)) - totalXYMean;
  const ssXX = sum(data.map((point) => point.x ** 2)) - totalSquareXMean;

  if (
    !assertAndContinue(ssXX !== 0, "ssXX should never be 0 if we checked that Xs aren't all the same value")
  ) {
    return 0;
  }

  return ssXY / ssXX;
}

export function fitIntercept(meanX: number, meanY: number, slopeResult: number): number {
  return meanY - slopeResult * meanX;
}

function calculateR2(
  data: Array<NumericCartesianPoint>,
  meanY: number,
  modelFit: LinearRegressionModelFit
): number {
  const residualsData = calculateResiduals(data, modelFit);
  const ssTotal = sum(residualsData.map((point) => (point.y - meanY) ** 2));
  const ssResiduals = sum(residualsData.map((point) => point.residual ** 2));

  const r2 = 1 - ssResiduals / ssTotal;

  // We define the case where y has no variance and thus gives a divide by zero error as just 1.
  return Number.isNaN(r2) ? 1 : r2;
}

export function calculateResiduals(
  data: Array<NumericCartesianPoint>,
  modelFit: LinearRegressionModelFit
): Array<NumericCartesianPoint & {residual: number}> {
  return data.map((point) => ({...point, residual: point.y - estimatePointY(point.x, modelFit)}));
}

export function estimatePointY(
  pointX: number,
  modelFit: Pick<LinearRegressionModelFit, 'B1' | 'B0'>
): number {
  return pointX * modelFit.B1 + modelFit.B0;
}

export function getFitEquation(intercept: number, slope: number): string {
  const interceptIsZero = intercept === 0;
  const slopeIsZero = slope === 0;

  const interceptString = interceptIsZero ? '' : prettyExpression(Math.abs(intercept), null);
  const slopeString = slopeIsZero ? '' : prettyExpression(slope, 'x');

  const interceptOperation = interceptIsZero || slopeIsZero ? '' : intercept < 0 ? ' - ' : ' + ';
  const equationString =
    interceptIsZero && slopeIsZero ? '0' : `${slopeString}${interceptOperation}${interceptString}`;

  return `y = ${equationString}`;
}
